// dependencies
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { useMatches } from "react-router-dom";
import { z } from "zod";

import { navigationConfigQuery } from "../../../api/queries/defs/navigation";

// api
import { type NavigationConfig, navigationConfigResponseSchema } from "../../services/defs/navigation";
import { PYUseQueryOptions } from "../StandardQueryArguments";

export type { NavigationConfig };

/**
 * Fetches navigation config for current viewing context.
 *
 * Automatically refetches when viewing different employees by extracting
 * employee ID from route params and using it as part of the query key.
 */
export const useNavigationConfig = (options?: PYUseQueryOptions<z.infer<typeof navigationConfigResponseSchema>>) => {
	const matches = useMatches();
	const queryClient = useQueryClient();

	// Extract employee ID from route params
	// Check multiple param names used across different routes
	const currentRoute = matches[matches.length - 1];
	const employeeId =
		(currentRoute?.params?.userID as string | undefined) ||
		(currentRoute?.params?.userId as string | undefined) ||
		(currentRoute?.params?.employeeID as string | undefined);

	const result = useQuery<z.infer<typeof navigationConfigResponseSchema>>(navigationConfigQuery(employeeId, options));

	// If the fetched data is for the current user viewing themselves,
	// populate the cache for both "self" and the user ID to prevent flashing
	useEffect(() => {
		if (result.data?.navigationConfig) {
			const { currentUserId, theUserId } = result.data.navigationConfig;

			// If viewing self, populate both cache keys
			if (currentUserId === theUserId) {
				// Set cache for "self" key
				queryClient.setQueryData(["navigation-config", "self"], result.data);

				// Set cache for the actual user ID key
				queryClient.setQueryData(["navigation-config", theUserId], result.data);
			}
		}
	}, [result.data, queryClient]);

	return result;
};
